// frontend/src/components/TermsGate.tsx
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trans, useTranslation } from 'react-i18next';

import { api, formatApiError } from '@/lib/api';

const LINKS = {
  av: '/legal/voorwaarden',
  vwo: '/legal/verwerkersovereenkomst',
  privacy: '/legal/privacy',
} as const;

/**
 * Republished terms have to be agreed to again, before anything else.
 *
 * Art. 6:233 sub b BW is not satisfied once and forever: a new version is a new
 * set of terms, and it binds only if it was handed over and accepted. A banner
 * somebody can scroll past is not acceptance -- so this stands in front of the
 * app, exactly as the tick on the sign-up form does.
 *
 * It fails open. If the check itself cannot be answered, the customer gets his
 * app: locking a paying customer out over a failed GET is a worse outcome than
 * a day's delay on a version bump.
 */
export function TermsGate({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const status = useQuery({
    queryKey: ['me', 'terms'],
    queryFn: () => api.me.terms(),
    retry: false,
    staleTime: 60 * 60_000,
  });

  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const accept = useMutation({
    mutationFn: () => api.me.acceptTerms(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['me', 'terms'] }),
    onError: (err) => setError(formatApiError(err, t('terms.saveFailed'))),
  });

  if (status.isPending || status.isError || !status.data?.outstanding.length) {
    return <>{children}</>;
  }

  return (
    <main className="min-h-screen bg-bg-primary text-text-primary grid place-items-center px-lg">
      <div className="w-full max-w-[28rem]">
        <h1 className="font-sans text-h2 mb-xs">{t('terms.title')}</h1>
        <p className="text-body-sm text-text-secondary mb-lg">{t('terms.intro')}</p>
        <div className="bg-bg-secondary border border-border-subtle rounded-md p-lg space-y-md">
          {error && <p className="text-body-sm text-danger">{error}</p>}
          <ul className="list-none m-0 p-0 space-y-xs text-body-sm">
            {status.data.outstanding.map((doc) => (
              <li key={doc}>
                <Link
                  to={LINKS[doc as keyof typeof LINKS] ?? '/legal/voorwaarden'}
                  target="_blank"
                  className="text-accent underline"
                >
                  {t(`terms.docs.${doc}`)}
                </Link>
              </li>
            ))}
          </ul>
          <label className="flex items-start gap-sm text-body-sm text-text-secondary">
            <input
              type="checkbox" checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              className="mt-[3px]"
            />
            <span>
              <Trans
                i18nKey="auth.termsAccept"
                components={{
                  av: <Link to={LINKS.av} target="_blank" className="underline hover:text-text-primary" />,
                  vwo: <Link to={LINKS.vwo} target="_blank" className="underline hover:text-text-primary" />,
                  privacy: <Link to={LINKS.privacy} target="_blank" className="underline hover:text-text-primary" />,
                }}
              />
            </span>
          </label>
          <button
            type="button"
            disabled={!accepted || accept.isPending}
            onClick={() => { setError(null); accept.mutate(); }}
            className="w-full bg-accent text-on-accent rounded-md py-sm text-body-sm font-medium disabled:opacity-60"
          >
            {accept.isPending ? t('terms.saving') : t('terms.accept')}
          </button>
        </div>
      </div>
    </main>
  );
}
